import { useEffect, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx';
import { useAnchoredStyle, useOutsidePress } from './popover';

/**
 * Popover menu anchored to a trigger (Create, account, team switcher).
 * role="menu"; ↑/↓/Home/End move between items, Esc closes and returns focus.
 */
export function Menu({
  open,
  onClose,
  anchorRef,
  label,
  width = 320,
  align = 'start',
  children,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  label: string;
  width?: number;
  align?: 'start' | 'end';
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const style = useAnchoredStyle(open, anchorRef, { gap: 8, maxHeight: 560, maxWidth: width, align });
  useOutsidePress(open, [anchorRef, panelRef], onClose);

  // Start on the item for the page you're on, else the first. (Focusing the
  // first item unconditionally made it look selected whatever the page.)
  useEffect(() => {
    if (!open || !style) return;
    const panel = panelRef.current;
    (panel?.querySelector<HTMLElement>('[role="menuitem"][aria-current="page"]') ?? panel?.querySelector<HTMLElement>('[role="menuitem"]'))?.focus();
  }, [open, style]);

  if (!open || !style) return null;

  const items = () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const i = list.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === 'ArrowDown') next = (i + 1) % list.length;
    else if (e.key === 'ArrowUp') next = (i - 1 + list.length) % list.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = list.length - 1;
    else if (e.key === 'Escape' || e.key === 'Tab') {
      if (e.key === 'Escape') e.preventDefault();
      onClose();
      anchorRef.current?.focus();
      return;
    }
    if (next >= 0) {
      e.preventDefault();
      list[next]?.focus();
    }
  };

  return createPortal(
    <div
      ref={panelRef}
      role="menu"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={{ ...style, width: Math.min(width, window.innerWidth - 16) }}
      className="wp-pop overflow-auto bg-modal border border-line2 rounded-[20px] p-2 shadow-wp"
    >
      {children}
    </div>,
    document.body,
  );
}

export function MenuItem({
  onSelect,
  children,
  hint,
  danger,
  current,
  className,
}: {
  onSelect: () => void;
  children: ReactNode;
  hint?: ReactNode;
  danger?: boolean;
  /** The page you're on: marked for screen readers and highlighted. */
  current?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      aria-current={current ? 'page' : undefined}
      onClick={onSelect}
      className={cx(
        // focus-visible, not focus: the menu focuses an item when it opens, and
        // a mouse user shouldn't see that as a highlight.
        'flex items-center justify-between gap-3 w-full text-left px-2.5 py-[9px] rounded-[11px] border-0 cursor-pointer text-[14px] hover:bg-fill focus-visible:bg-fill focus-visible:outline-none',
        current ? 'bg-fill2 font-semibold' : 'bg-transparent',
        danger ? 'text-acc' : 'text-t1',
        className,
      )}
    >
      <span className="min-w-0 flex-1 flex items-center gap-2">
        {current && <span className="w-1.5 h-1.5 rounded-full bg-acc shrink-0" aria-hidden="true" />}
        {children}
      </span>
      {hint && <span className="text-[12px] text-t5 shrink-0 font-normal">{hint}</span>}
    </button>
  );
}

/** Rich menu row with an icon tile and a sub-line (Create menu, team switcher). */
export function MenuTileItem({
  onSelect,
  glyph,
  tone = 'neutral',
  title,
  sub,
  trailing,
  selected,
}: {
  onSelect: () => void;
  glyph?: ReactNode;
  tone?: 'accent' | 'neutral' | 'amber';
  title: ReactNode;
  sub?: ReactNode;
  trailing?: ReactNode;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      aria-current={selected || undefined}
      onClick={onSelect}
      className={cx(
        'flex items-center gap-3 w-full text-left p-2.5 rounded-[13px] border-0 cursor-pointer text-t1 hover:bg-fill focus-visible:bg-fill focus-visible:outline-none',
        selected ? 'bg-acc-bg' : 'bg-transparent',
      )}
    >
      {glyph && (
        <span
          aria-hidden="true"
          className={cx(
            'w-[38px] h-[38px] rounded-[11px] flex items-center justify-center text-[17px] font-semibold shrink-0',
            tone === 'accent' && 'bg-acc-bg2 text-acc',
            tone === 'neutral' && 'bg-fill2 text-t1',
            tone === 'amber' && 'bg-amb-bg text-amb',
          )}
        >
          {glyph}
        </span>
      )}
      <span className="flex-1 min-w-0">
        <span className="block text-[14.5px] font-semibold">{title}</span>
        {sub && <span className="block text-[12.5px] text-t4 mt-px">{sub}</span>}
      </span>
      {trailing}
    </button>
  );
}

export const MenuDivider = () => <div className="h-px bg-line my-1.5 mx-1" role="separator" />;
export const MenuLabel = ({ children }: { children: ReactNode }) => (
  <div className="text-[11px] font-semibold tracking-[.08em] uppercase text-t4 px-2.5 pt-2 pb-1.5">{children}</div>
);
